import { Auth } from "aws-amplify";

/**
 * Single client for the pyseowind Flask API (py.seowind.io / py-staging.seowind.io).
 *
 * Every call carries the signed-in user's Cognito ID token as a Bearer header so
 * the backend can verify who is asking and whether the brief belongs to them.
 * Previously each slice had its own copy of the base-URL helper and sent bare
 * `fetch` calls with no credentials.
 */

export const getPyApiBaseUrl = (): string => {
  if (
    process.env.NEXT_PUBLIC_BRANCH === "dev" ||
    process.env.NODE_ENV === "development"
  ) {
    return "https://py-staging.seowind.io";
  }
  if (process.env.NEXT_PUBLIC_BRANCH === "staging") {
    return "https://py-staging.seowind.io";
  }
  return "https://py.seowind.io";
};

export class PyApiError extends Error {
  status: number;
  body?: any;

  constructor(status: number, message: string, body?: any) {
    super(message);
    this.name = "PyApiError";
    this.status = status;
    this.body = body;
  }
}

const getIdToken = async (): Promise<string | null> => {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch {
    // Not signed in (or session unavailable): send without a header and let the
    // backend answer 401 rather than failing before the request is made.
    return null;
  }
};

/** Force Amplify to refresh the session from the refresh token. */
const refreshIdToken = async (): Promise<string | null> => {
  try {
    const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
    const session = await Auth.currentSession();
    await new Promise<void>((resolve) => {
      user.refreshSession(session.getRefreshToken(), () => resolve());
    });
    return (await Auth.currentSession()).getIdToken().getJwtToken();
  } catch {
    return null;
  }
};

type PyFetchOptions = {
  /** Retry once with a refreshed token when the server answers 401. Default true. */
  retryOn401?: boolean;
};

/**
 * `fetch` against pyseowind with the Bearer ID token attached.
 *
 * Returns the raw `Response` for anything other than 401/403 so callers keep
 * their existing `json.status_code` handling. A 401 is retried once with a
 * refreshed token; a persistent 401 or a 403 throws `PyApiError`.
 */
export const pyFetch = async (
  path: string,
  init: RequestInit = {},
  options: PyFetchOptions = {}
): Promise<Response> => {
  const { retryOn401 = true } = options;
  const headers = new Headers(init.headers || {});

  const token = await getIdToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  // Only JSON string bodies get a default content type; FormData must keep its
  // own multipart boundary.
  if (typeof init.body === "string" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`${getPyApiBaseUrl()}${path}`, { ...init, headers });

  if (response.status === 401 && retryOn401) {
    const fresh = await refreshIdToken();
    if (fresh) {
      headers.set("Authorization", `Bearer ${fresh}`);
      return pyFetch(path, { ...init, headers }, { retryOn401: false });
    }
  }
  if (response.status === 401 || response.status === 403) {
    let body: any = undefined;
    try {
      body = await response.clone().json();
    } catch {
      /* non-JSON error body */
    }
    throw new PyApiError(
      response.status,
      body?.data?.message || `Request to ${path} was refused (${response.status})`,
      body
    );
  }
  return response;
};
